import React from 'react';
import { WorkplaceDocumentItem, FolderNode } from '../../services/api';

interface FileGridProps {
  subFolders: FolderNode[];
  documents: WorkplaceDocumentItem[];
  onNavigate: (folderId: number | null) => void;
  isLoading: boolean;
  onPreview: (doc: WorkplaceDocumentItem) => void;
  onDownload: (doc: WorkplaceDocumentItem) => void;
  onDelete: (doc: WorkplaceDocumentItem) => void;
  docTypeLabels: Record<string, string>;
  viewMode: 'grid' | 'list';
  currentFolder?: FolderNode | null;
  ancestors?: Array<{ id: number; name: string; type: string }>;
  workplaceTitle?: string;
  onSelectWorkplace?: (workplaceId: number) => void;
  selectedDocIds?: number[];
  onToggleSelect?: (docId: number) => void;
  onSelectAll?: () => void;
}

const FileGrid: React.FC<FileGridProps> = ({
  subFolders,
  documents,
  onNavigate,
  isLoading,
  onPreview,
  onDownload,
  onDelete,
  docTypeLabels,
  viewMode,
  currentFolder,
  ancestors = [],
  workplaceTitle,
  onSelectWorkplace,
  selectedDocIds = [],
  onToggleSelect,
  onSelectAll
}) => {
  const getBreadcrumbIcon = (type: string) => {
    if (type === 'year') return '📅';
    if (type === 'month') return '🗓️';
    return '📁';
  };

  const renderBreadcrumbBar = () => {
    if (!workplaceTitle && !currentFolder) return null;

    return (
      <div className="archive-breadcrumbs-bar" style={{ marginBottom: '16px' }}>
        <button
          type="button"
          className={`breadcrumb-item ${!currentFolder ? 'active' : ''}`}
          onClick={() => onNavigate(null)}
        >
          🏢 {workplaceTitle || 'Kök Dizin'}
        </button>

        {/* Ancestor folders chain (e.g. Risk Analizi -> 2026) */}
        {ancestors.map((anc) => (
          <React.Fragment key={anc.id}>
            <span className="breadcrumb-divider">/</span>
            <button
              type="button"
              className="breadcrumb-item"
              onClick={() => onNavigate(anc.id)}
            >
              {getBreadcrumbIcon(anc.type)} {anc.name}
            </button>
          </React.Fragment>
        ))}

        {/* Current Folder (e.g. Ekim) */}
        {currentFolder && (
          <>
            <span className="breadcrumb-divider">/</span>
            <span className="breadcrumb-item active">
              {getBreadcrumbIcon(currentFolder.type)} {currentFolder.name}
            </span>
            <button
              type="button"
              className="breadcrumb-back-btn"
              onClick={() => onNavigate(currentFolder.parentFolderId || null)}
              title="Üst Klasöre Dön"
            >
              ⬅️ Üst Dizine Dön
            </button>
          </>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="archive-loading-state">
        <div className="archive-spinner"></div>
        <p>İçerikler yükleniyor...</p>
      </div>
    );
  }

  if (subFolders.length === 0 && documents.length === 0) {
    return (
      <div className="archive-empty-folder">
        {renderBreadcrumbBar()}
        <div className="empty-folder-icon">📂</div>
        <h3>{currentFolder ? 'Bu Klasör Boş' : 'Bu İşyerinde Henüz Belge Bulunmuyor'}</h3>
        <p>
          {currentFolder
            ? 'Burada henüz kayıtlı belge veya alt klasör bulunmuyor.'
            : 'Bu işyerine ait yüklenmiş bir belge yok. Belge yüklediğinizde ilgili kategori, yıl ve ay klasörleri otomatik olarak oluşturulacaktır.'}
        </p>
      </div>
    );
  }

  const formatFileSize = (bytes: number) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const getFileIcon = (fileName: string) => {
    const ext = fileName?.split('.').pop()?.toLowerCase() || '';
    if (ext === 'pdf') {
      return { icon: '📕', color: '#ef4444', label: 'PDF' };
    }
    if (['doc', 'docx'].includes(ext)) {
      return { icon: '📘', color: '#2563eb', label: 'WORD' };
    }
    if (['xls', 'xlsx'].includes(ext)) {
      return { icon: '📗', color: '#16a34a', label: 'EXCEL' };
    }
    if (['jpg', 'jpeg', 'png', 'webp'].includes(ext)) {
      return { icon: '🖼️', color: '#f59e0b', label: 'RESİM' };
    }
    return { icon: '📄', color: '#64748b', label: 'DOSYA' };
  };

  const formatExpiryCountdown = (days: number | null) => {
    if (days === null) return null;
    if (days < 0) {
      const positive = Math.abs(days);
      if (positive > 365) {
        const years = (positive / 365).toFixed(1);
        return `${years} yıl gecikti`;
      }
      return `${positive} gün gecikti`;
    }
    if (days === 0) return 'Bugün doluyor';
    if (days > 365) {
      const years = (days / 365).toFixed(1);
      return `${years} yıl kaldı`;
    }
    if (days > 30) {
      const months = Math.floor(days / 30);
      return `${months} ay kaldı`;
    }
    return `${days} gün kaldı`;
  };

  return (
    <div className="archive-file-grid-container">
      {/* Gezinme / Ekmek Kırıntısı (Breadcrumb) */}
      {renderBreadcrumbBar()}

      {/* 1. Alt Klasörler Bölümü */}
      {subFolders.length > 0 && (
        <div className="archive-section-block">
          <h4 className="archive-section-title">
            <span>📁</span> Alt Klasörler ({subFolders.length})
          </h4>
          <div className="archive-subfolders-grid">
            {subFolders.map((folder) => {
              const isYear = folder.type === 'year';
              const isMonth = folder.type === 'month';
              const cardClass = isYear ? 'year-card' : isMonth ? 'month-card' : '';
              const folderIcon = isYear ? '📅' : isMonth ? '🗓️' : '📁';

              return (
                <div
                  key={folder.id}
                  className={`archive-folder-card ${cardClass}`}
                  onClick={() => onNavigate(folder.id)}
                  title={`${folder.name} klasörüne git`}
                >
                  <span className="folder-card-icon">{folderIcon}</span>
                  <div className="folder-card-info">
                    <span className="folder-card-name">{folder.name}</span>
                    {folder.docCount !== undefined && folder.docCount > 0 && (
                      <span className="folder-card-count">{folder.docCount} Belge</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 2. Belgeler Bölümü */}
      {documents.length > 0 && (
        <div className="archive-section-block">
          <h4 className="archive-section-title">
            <span>📄</span> {currentFolder ? 'Klasördeki Belgeler' : 'Tüm Resmi Belgeler'} ({documents.length})
          </h4>

          {viewMode === 'grid' ? (
            <div className="archive-docs-grid">
              {documents.map((doc) => {
                const { icon, color, label } = getFileIcon(doc.fileName || doc.originalFileName);
                const categoryLabel = docTypeLabels[doc.documentType] || doc.documentType;
                const countdown = formatExpiryCountdown(doc.daysUntilExpiry);
                const workplaceName = (doc as any).workplace?.title || (doc as any).Workplace?.title;

                return (
                  <div key={doc.id} className={`archive-doc-card status-${doc.expiryStatus} ${selectedDocIds.includes(doc.id) ? 'selected' : ''}`}>
                    {/* Üst Kısım: Tür ve Durum Rozeti */}
                    <div className="doc-card-header">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {onToggleSelect && (
                          <input
                            type="checkbox"
                            checked={selectedDocIds.includes(doc.id)}
                            onChange={() => onToggleSelect(doc.id)}
                            style={{ cursor: 'pointer', width: '15px', height: '15px', accentColor: '#2563eb' }}
                            title="Seç"
                          />
                        )}
                        <span className="doc-category-badge" title={categoryLabel}>
                          {categoryLabel}
                        </span>
                      </div>
                      <span className={`doc-status-pill ${doc.expiryStatus}`}>
                        {doc.expiryStatus === 'valid' && '✅ Geçerli'}
                        {doc.expiryStatus === 'expiring' && '⚠️ Yaklaşıyor'}
                        {doc.expiryStatus === 'expired' && '❌ Süresi Doldu'}
                        {doc.expiryStatus === 'permanent' && '♾️ Süresiz'}
                      </span>
                    </div>

                    {/* İşyeri Bilgisi */}
                    {workplaceName && (
                      <div
                        className="doc-card-workplace-row"
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelectWorkplace?.(doc.workplaceId);
                        }}
                        title={`İşyeri klasörlerini aç: ${workplaceName}`}
                      >
                        <span className="doc-workplace-chip">🏢 {workplaceName}</span>
                      </div>
                    )}

                    {/* Orta Kısım: İkon ve Dosya İsmi */}
                    <div className="doc-card-body" onClick={() => onPreview(doc)}>
                      <div className="doc-file-icon" style={{ color }}>
                        {icon}
                      </div>
                      <div className="doc-file-info">
                        <strong className="doc-file-name" title={doc.originalFileName}>
                          {doc.originalFileName}
                        </strong>
                        <span className="doc-file-meta">
                          {label} • {formatFileSize(doc.fileSize)}
                        </span>
                      </div>
                    </div>

                    {/* Kalan Süre / Bitiş Tarihi */}
                    <div className="doc-card-expiry-row">
                      {countdown && (
                        <span className={`countdown-tag ${doc.expiryStatus}`}>
                          ⏳ {countdown}
                        </span>
                      )}
                      <span className="upload-date-text">
                        📅 {doc.documentDate || doc.uploadedDate}
                      </span>
                    </div>

                    {/* Alt Eylem Butonları */}
                    <div className="doc-card-actions">
                      <button
                        type="button"
                        className="doc-action-btn preview"
                        onClick={() => onPreview(doc)}
                        title="Önizle"
                      >
                        👁️ Önizle
                      </button>
                      <button
                        type="button"
                        className="doc-action-btn download"
                        onClick={() => onDownload(doc)}
                        title="İndir"
                      >
                        📥 İndir
                      </button>
                      <button
                        type="button"
                        className="doc-action-btn delete"
                        onClick={() => onDelete(doc)}
                        title="Sil"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* Liste Görünümü */
            <div className="archive-docs-table-wrapper">
              <table className="archive-docs-table">
                <thead>
                  <tr>
                    {onToggleSelect && (
                      <th style={{ width: '40px', textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={documents.length > 0 && selectedDocIds.length === documents.length}
                          onChange={onSelectAll}
                          style={{ cursor: 'pointer', width: '15px', height: '15px', accentColor: '#2563eb' }}
                          title="Tümünü Seç / Seçimi Kaldır"
                        />
                      </th>
                    )}
                    <th>Dosya Adı</th>
                    <th>Evrak Türü</th>
                    <th>İşyeri</th>
                    <th>Belge Tarihi</th>
                    <th>Boyut</th>
                    <th>Durum</th>
                    <th>İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((doc) => {
                    const { icon, color } = getFileIcon(doc.fileName || doc.originalFileName);
                    const categoryLabel = docTypeLabels[doc.documentType] || doc.documentType;
                    const workplaceName = (doc as any).workplace?.title || (doc as any).Workplace?.title;

                    return (
                      <tr key={doc.id} className={`archive-table-row status-${doc.expiryStatus} ${selectedDocIds.includes(doc.id) ? 'selected' : ''}`}>
                        {onToggleSelect && (
                          <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedDocIds.includes(doc.id)}
                              onChange={() => onToggleSelect(doc.id)}
                              style={{ cursor: 'pointer', width: '15px', height: '15px', accentColor: '#2563eb' }}
                            />
                          </td>
                        )}
                        <td className="doc-table-name-cell" onClick={() => onPreview(doc)}>
                          <span className="table-file-icon" style={{ color }}>{icon}</span>
                          <strong className="table-file-title" title={doc.originalFileName}>
                            {doc.originalFileName}
                          </strong>
                        </td>
                        <td>{categoryLabel}</td>
                        <td
                          className="doc-table-workplace-cell"
                          onClick={() => onSelectWorkplace?.(doc.workplaceId)}
                          style={{ cursor: onSelectWorkplace ? 'pointer' : 'default', color: '#1d4ed8', fontWeight: 600 }}
                          title="İşyeri klasörlerini aç"
                        >
                          🏢 {workplaceName || '-'}
                        </td>
                        <td>{doc.documentDate || doc.uploadedDate}</td>
                        <td>{formatFileSize(doc.fileSize)}</td>
                        <td>
                          <span className={`doc-status-pill ${doc.expiryStatus}`}>
                            {doc.expiryStatus === 'valid' && '✅ Geçerli'}
                            {doc.expiryStatus === 'expiring' && '⚠️ Yaklaşıyor'}
                            {doc.expiryStatus === 'expired' && '❌ Süresi Doldu'}
                            {doc.expiryStatus === 'permanent' && '♾️ Süresiz'}
                          </span>
                        </td>
                        <td>
                          <div className="table-action-btns">
                            <button
                              type="button"
                              className="table-action-btn"
                              onClick={() => onPreview(doc)}
                              title="Önizle"
                            >
                              👁️
                            </button>
                            <button
                              type="button"
                              className="table-action-btn"
                              onClick={() => onDownload(doc)}
                              title="İndir"
                            >
                              📥
                            </button>
                            <button
                              type="button"
                              className="table-action-btn delete"
                              onClick={() => onDelete(doc)}
                              title="Sil"
                            >
                              🗑️
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FileGrid;
